/* eslint-disable react/jsx-no-bind */
/* eslint-disable id-length */

import "./style.css";

import { useCallback, useContext, useEffect, useState } from "react";
import { type Node, ReactFlow, ReactFlowProvider, useReactFlow } from "@xyflow/react";
import { CommandStep, NodeType } from "app/lib/pipeline";
import { edgeTypes } from "./Edge";
import Background from "./Background";
import Header from "app/components/Playground/Header";
import SidePanel from "./SidePanel";
import * as HoverCard from "./HoverCard";
import usePositionedGraph from "./usePositionedGraph";
import Controls from "./Controls/Controls";
import classNames from "classnames";
import { nodeTypes } from "./Node";
import { useGraph } from "./useGraph";
import Spinner from "app/components/shared/Spinner";

const ZOOM_PADDING = 0.2;

interface Props {
  // eslint-disable-next-line react/no-unused-prop-types
  mode: "default" | "embed" | "build";
  // eslint-disable-next-line react/no-unused-prop-types
  templateID?: string;
}

/**
 * An (experimental) interactive pipeline playground with a side panel for editing commands.
 *
 * This playground is used for creating and editing pipeline configuration and rendering the pipeline graph.
 */
function Playground(props: Props) {
  const graph = useGraph(props);
  const { fitView } = useReactFlow();

  const fitGraph = useCallback(() => {
    fitView({ padding: ZOOM_PADDING });
  }, [fitView]);

  const { nodes, edges, initialised, calculatingLayout, onNodesChange, onEdgesChange } =
    usePositionedGraph({
      ...graph,
      onInitialised: fitGraph,
    });

  const [open, setOpen] = useState(false);
  const [openNode, setOpenNode] = useState<Node<CommandStep> | undefined>(undefined);
  const handleNodeClick = useCallback(
    (_event, node: Node) => {
      // only open panel for command nodes for the time being
      if (node.type === NodeType.Command) {
        setOpen(true);
        setOpenNode(node as Node<CommandStep>);
      } else {
        setOpen(false);
      }
    },
    [setOpen, setOpenNode],
  );
  const handlePanelClick = useCallback(() => setOpen(false), [setOpen]);

  return (
    <div className="h-full w-full">
      <Header id={props.templateID} />
      <ReactFlow
        nodes={nodes}
        edges={edges}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        fitView={true}
        nodeTypes={nodeTypes}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePanelClick}
        proOptions={{ hideAttribution: true }}
        nodesConnectable={false}
      >
        <HoverCard.Body />
        {initialised && <Background />}
        {calculatingLayout && <Spinner className="animate-delay-fade-in absolute right-4 top-4" />}
      </ReactFlow>
      <SidePanel open={open} setOpen={setOpen} data={openNode?.data} />
    </div>
  );
}

// Allow scrolling _only_ when control or command key is pressed.
// This is to prevent the parent page from scrolling when the playground is embedded.
const useWaitForScroll = () => {
  const [scrollable, setScrollable] = useState(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Control" || e.key === "Meta") {
        setScrollable(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Control" || e.key === "Meta") {
        setScrollable(false);
      }
    };

    addEventListener("keydown", handleKeyDown);
    addEventListener("keyup", handleKeyUp);
    return () => {
      removeEventListener("keydown", handleKeyDown);
      removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  return scrollable;
};

/**
 * A read-only version of the playground with disabled scrolling and zooming
 * until the user interacts with the playground.
 *
 * Used for embedding pipeline templates within an iframe.
 */
function EmbedPlayground(props: Props) {
  const graph = useGraph(props);
  const { fitView } = useReactFlow();

  const fitGraph = useCallback(() => {
    fitView({ padding: ZOOM_PADDING });
  }, [fitView]);

  const { nodes, edges, initialised, onNodesChange, onEdgesChange } = usePositionedGraph({
    ...graph,
    onInitialised: fitGraph,
  });

  const scrollable = useWaitForScroll();
  const [, dispatch] = useContext(HoverCard.Context);

  const handleOnMoveStart = useCallback(() => {
    // prevents HoverCard from being shown in incorrect positions whilst it's closing
    dispatch({ type: HoverCard.ActionType.Close });
  }, []);

  return (
    <div
      className={classNames(`h-full w-full`, {
        // nowheel is a class that prevents ReactFlow from scrolling
        nowheel: !scrollable,
      })}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        edgeTypes={edgeTypes}
        edgesReconnectable={false}
        fitView={true}
        nodeTypes={nodeTypes}
        nodesConnectable={false}
        onMoveStart={handleOnMoveStart}
        panOnScroll={false}
        proOptions={{ hideAttribution: true }}
        elementsSelectable={false}
        zoomOnScroll={false}
        preventScrolling={true}
        minZoom={0.1}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
      >
        {initialised && <Controls fitViewOptions={{ padding: ZOOM_PADDING }} />}
        <HoverCard.Body />
        {initialised && <Background />}
      </ReactFlow>
    </div>
  );
}

export default (props: Props) => {
  return (
    <ReactFlowProvider>
      <HoverCard.Provider>
        {props.mode === "embed" && <EmbedPlayground {...props} />}
        {props.mode === "default" && <Playground {...props} />}
      </HoverCard.Provider>
    </ReactFlowProvider>
  );
};
